import React from 'react';

interface LogoProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'full' | 'icon' | 'light';
  showTagline?: boolean;
}

export const Logo: React.FC<LogoProps> = ({
  className = '',
  size = 'md',
  variant = 'full',
  showTagline = false
}) => {
  // Height scale
  const dimensions = {
    sm: { height: 36, fontSize: 'text-[11px]' },
    md: { height: 50, fontSize: 'text-xs' },
    lg: { height: 68, fontSize: 'text-sm' },
    xl: { height: 92, fontSize: 'text-base' }
  }[size];

  const subTextColor = variant === 'light' ? 'text-slate-300' : 'text-slate-600';

  return (
    <div className={`inline-flex flex-col ${className}`}>
      <div className="flex items-center gap-2">
        {/* Exact Official ASAP LEARNING Logo Vector */}
        <svg
          viewBox="0 0 540 160"
          style={{ height: `${dimensions.height}px`, width: 'auto' }}
          className="overflow-visible select-none transition-transform hover:scale-[1.01] duration-200"
          aria-label="ASAP LEARNING Logo"
        >
          <defs>
            {/* Left Leg Gradient: Rose Red -> Purple */}
            <linearGradient id="asap-leg-left" x1="0%" y1="100%" x2="70%" y2="0%">
              <stop offset="0%" stopColor="#DE315A" />
              <stop offset="50%" stopColor="#A82A82" />
              <stop offset="100%" stopColor="#7B3293" />
            </linearGradient>

            {/* Right Leg Gradient: Blue */}
            <linearGradient id="asap-leg-right" x1="20%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#0D7AC2" />
              <stop offset="100%" stopColor="#0066B3" />
            </linearGradient>

            {/* Crossbar Gradient: Purple/Violet */}
            <linearGradient id="asap-crossbar" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#9C3B9B" />
              <stop offset="100%" stopColor="#7B3293" />
            </linearGradient>
          </defs>

          {/* Left Graphic Symbol 'A' */}
          <g transform="translate(8, 8)">
            {/* Left Arch/Leg (Pink to Purple) */}
            <path
              d="M 24 140 C 8 140 2 120 18 95 L 68 22 C 76 10 90 8 100 24 L 110 40 C 88 72 62 106 38 136 C 33 141 28 140 24 140 Z"
              fill="url(#asap-leg-left)"
            />

            {/* Right Leg (Deep Blue) */}
            <path
              d="M 88 12 L 142 120 C 150 136 136 144 122 136 L 94 84 C 105 56 96 28 88 12 Z"
              fill="url(#asap-leg-right)"
            />

            {/* Middle Crossbar Loop (Purple) */}
            <path
              d="M 28 94 C 58 76, 104 72, 136 96 C 120 110, 88 114, 58 104 C 45 100, 36 97, 28 94 Z"
              fill="url(#asap-crossbar)"
            />
          </g>

          {variant !== 'icon' && (
            <g>
              {/* TOP ROW: "SAP®" */}
              <g transform="translate(175, 62)">
                {/* S */}
                <text
                  x="0"
                  y="0"
                  fill={variant === 'light' ? '#38BDF8' : '#0B72B9'}
                  fontSize="58"
                  fontWeight="900"
                  fontFamily="system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
                  letterSpacing="-0.5"
                >
                  S
                </text>

                {/* A */}
                <text
                  x="38"
                  y="0"
                  fill={variant === 'light' ? '#38BDF8' : '#0B72B9'}
                  fontSize="58"
                  fontWeight="900"
                  fontFamily="system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
                  letterSpacing="-0.5"
                >
                  A
                </text>

                {/* P */}
                <text
                  x="80"
                  y="0"
                  fill={variant === 'light' ? '#38BDF8' : '#0B72B9'}
                  fontSize="58"
                  fontWeight="900"
                  fontFamily="system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
                  letterSpacing="-0.5"
                >
                  P
                </text>

                {/* Green corner triangle slice on top-right loop of 'P' */}
                <polygon points="108,-43 121,-43 121,-28" fill="#3B9843" />

                {/* ® Registered Symbol */}
                <text
                  x="125"
                  y="-28"
                  fill={variant === 'light' ? '#38BDF8' : '#0B72B9'}
                  fontSize="22"
                  fontWeight="700"
                >
                  ®
                </text>
              </g>

              {/* BOTTOM ROW: "Learning" */}
              <g transform="translate(175, 134)">
                {/* L e a r */}
                <text
                  x="0"
                  y="0"
                  fill={variant === 'light' ? '#38BDF8' : '#0B72B9'}
                  fontSize="64"
                  fontWeight="800"
                  fontFamily="system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
                  letterSpacing="-0.5"
                >
                  Lear
                </text>

                {/* 'n' (first n) in Blue with Green top-right wedge */}
                <g transform="translate(138, 0)">
                  <text
                    x="0"
                    y="0"
                    fill={variant === 'light' ? '#38BDF8' : '#0B72B9'}
                    fontSize="64"
                    fontWeight="800"
                    fontFamily="system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
                  >
                    n
                  </text>
                  {/* Green triangle wedge on top-right of 'n' */}
                  <polygon points="26,-44 36,-44 36,-31" fill="#3B9843" />
                </g>

                {/* 'i' in Green with 5-Point Star as dot */}
                <g transform="translate(176, 0)">
                  {/* Stem of 'i' */}
                  <rect x="2" y="-41" width="12" height="41" fill="#3B9843" rx="1" />

                  {/* 5-pointed Green Star Replacing 'i' Dot */}
                  <polygon
                    points="8,-59 11,-49 20,-49 13,-44 15,-35 8,-40 1,-35 3,-44 -4,-49 5,-49"
                    fill="#3B9843"
                  />
                </g>

                {/* 'n' (second n) in Green */}
                <text
                  x="196"
                  y="0"
                  fill="#3B9843"
                  fontSize="64"
                  fontWeight="800"
                  fontFamily="system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
                >
                  n
                </text>

                {/* 'g' in Green */}
                <text
                  x="236"
                  y="0"
                  fill="#3B9843"
                  fontSize="64"
                  fontWeight="800"
                  fontFamily="system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
                >
                  g
                </text>
              </g>
            </g>
          )}
        </svg>
      </div>

      {showTagline && (
        <span className={`mt-1 font-semibold tracking-wide ${dimensions.fontSize} ${subTextColor}`}>
          AI-Driven Publishing Solutions
        </span>
      )}
    </div>
  );
};


